import { TestTypes } from '@hawkindynamics/core-client';
import dayjs from 'dayjs';
import { doc, DocumentReference } from 'firebase/firestore';
import isEmpty from 'lodash/isEmpty';
import { docData } from 'rxfire/firestore';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IScoreboardSettings, IUser } from '../../types';
import { IMetrics, metrics } from '../metricsProvider';
import { authenticatedUserProvider } from '../userProvider';
const TEST_TYPES = TestTypes.TEST_TYPES;

/**
 * @description the default values for scoreboard settings
 */
const createDefaultScoreboardSettings = (
  allMetrics: IMetrics
): IScoreboardSettings => {
  const rangeStart = dayjs().utc().subtract(1, 'month');
  const rangeEnd = dayjs().utc().endOf('day');
  return {
    dateRangeType: { label: 'Last Month', value: 'last-month' },
    dateRange: {
      from: {
        label: rangeStart.format('MM/DD/YY'),
        value: rangeStart.valueOf(),
      },
      to: { label: rangeEnd.format('MM/DD/YY'), value: rangeEnd.valueOf() },
    },
    testType: {
      label: TEST_TYPES.CMJ.name,
      value: TEST_TYPES.CMJ.id,
      entity: TEST_TYPES.CMJ,
    },
    tags: [],
    filteredTeams: [],
    filteredGroups: [],
    metrics: {
      [TEST_TYPES.CMJ.id]: {
        label: allMetrics[TEST_TYPES.CMJ.id][TEST_TYPES.CMJ.defaultKey].label,
        value: TEST_TYPES.CMJ.defaultKey,
        entity: allMetrics[TEST_TYPES.CMJ.id][TEST_TYPES.CMJ.defaultKey],
      },
      [TEST_TYPES.SQUAT.id]: {
        label:
          allMetrics[TEST_TYPES.SQUAT.id][TEST_TYPES.SQUAT.defaultKey].label,
        value: TEST_TYPES.SQUAT.defaultKey,
        entity: allMetrics[TEST_TYPES.SQUAT.id][TEST_TYPES.SQUAT.defaultKey],
      },
      [TEST_TYPES.DROP.id]: {
        label: allMetrics[TEST_TYPES.DROP.id][TEST_TYPES.DROP.defaultKey].label,
        value: TEST_TYPES.DROP.defaultKey,
        entity: allMetrics[TEST_TYPES.DROP.id][TEST_TYPES.DROP.defaultKey],
      },
      [TEST_TYPES.FREE.id]: {
        label: allMetrics[TEST_TYPES.FREE.id][TEST_TYPES.FREE.defaultKey].label,
        value: TEST_TYPES.FREE.defaultKey,
        entity: allMetrics[TEST_TYPES.FREE.id][TEST_TYPES.FREE.defaultKey],
      },
      [TEST_TYPES.REBOUND.id]: {
        label:
          allMetrics[TEST_TYPES.REBOUND.id][TEST_TYPES.REBOUND.defaultKey]
            .label,
        value: TEST_TYPES.REBOUND.defaultKey,
        entity:
          allMetrics[TEST_TYPES.REBOUND.id][TEST_TYPES.REBOUND.defaultKey],
      },
      [TEST_TYPES.MULTI.id]: {
        label:
          allMetrics[TEST_TYPES.MULTI.id][TEST_TYPES.MULTI.defaultKey].label,
        value: TEST_TYPES.MULTI.defaultKey,
        entity: allMetrics[TEST_TYPES.MULTI.id][TEST_TYPES.MULTI.defaultKey],
      },
      [TEST_TYPES.ISO.id]: {
        label: allMetrics[TEST_TYPES.ISO.id][TEST_TYPES.ISO.defaultKey].label,
        value: TEST_TYPES.ISO.defaultKey,
        entity: allMetrics[TEST_TYPES.ISO.id][TEST_TYPES.ISO.defaultKey],
      },
      [TEST_TYPES.WEIGH_IN.id]: {
        label:
          allMetrics[TEST_TYPES.WEIGH_IN.id][TEST_TYPES.WEIGH_IN.defaultKey]
            .label,
        value: TEST_TYPES.WEIGH_IN.defaultKey,
        entity:
          allMetrics[TEST_TYPES.WEIGH_IN.id][TEST_TYPES.WEIGH_IN.defaultKey],
      },
      [TEST_TYPES.DROP_LANDING.id]: {
        label:
          allMetrics[TEST_TYPES.DROP_LANDING.id][
            TEST_TYPES.DROP_LANDING.defaultKey
          ].label,
        value: TEST_TYPES.DROP_LANDING.defaultKey,
        entity:
          allMetrics[TEST_TYPES.DROP_LANDING.id][
            TEST_TYPES.DROP_LANDING.defaultKey
          ],
      },
    },
  };
};

/**
 * @description observes the users scoreboard settings
 */
export const scoreboardSettingsProvider =
  (): Observable<IScoreboardSettings> => {
    return authenticatedUserProvider().pipe(
      /// subscribe to the users scoreboard settings

      switchMap(
        (user: IUser): Observable<IScoreboardSettings> =>
          docData(
            doc(
              user.services.firestore,
              `userProfiles/${user.id}/settings/scoreboard`
            ) as DocumentReference<IScoreboardSettings>
          )
      ),
      /// if the settings are currently empty then return the default values instead
      switchMap(
        (settings: IScoreboardSettings): Observable<IScoreboardSettings> => {
          if (isEmpty(settings)) {
            return metrics.pipe(map(createDefaultScoreboardSettings));
          }
          return of(settings);
        }
      )
    );
  };
